import { Alert, Checkbox, Input, message, Modal, Typography } from 'antd'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import { useDuplicateActiveThreads } from 'features/libra/comments/commentsApi'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { useCreateTicketVersion } from 'features/libra/ticket/libraTicketsApi'
import {
  resolveTicketVersionZoneFields,
  type TicketVersionZoneFieldsResult,
} from 'features/libra/ticket/ticketVersionZoneFields'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import type { ChangeEvent, FC } from 'react'
import { useCallback, useState } from 'react'
import styled from 'styled-components'

const { Text } = Typography
const { TextArea } = Input

function getUnavailableMessage(
  result: Extract<TicketVersionZoneFieldsResult, { status: 'unavailable' }>
): string {
  if (result.reason === 'workflow-unavailable')
    return 'This ticket’s workflow is unavailable. Refresh the page and try again.'
  if (result.reason === 'workflow-mismatch')
    return 'The loaded workflow does not match this ticket. Refresh the page and try again.'
  return 'The workflow has an invalid zone field configuration. Ask a workflow administrator to review it before creating a version.'
}

export const CreateVersionModal: FC<{
  isOpen: boolean
  sourceTicket?: LibraTicket
  sourceWorkflow?: LibraWorkflow
  onClose: () => void
  onSuccess: (
    newTicketId: string,
    sourceTicket: LibraTicket
  ) => void | Promise<void>
}> = ({ isOpen, sourceTicket, sourceWorkflow, onClose, onSuccess }) => {
  const createTicketVersion = useCreateTicketVersion()
  const duplicateActiveThreads = useDuplicateActiveThreads(sourceTicket?.id)
  const [isCreating, setIsCreating] = useState(false)
  const [versionJustification, setVersionReason] = useState('')
  const [copyThreads, setCopyThreads] = useState(false)
  const [messageApi, contextHolder] = message.useMessage()
  const zoneFieldResolution = sourceTicket
    ? resolveTicketVersionZoneFields(sourceTicket, sourceWorkflow)
    : undefined
  const zoneFieldTargets =
    zoneFieldResolution?.status === 'ready'
      ? zoneFieldResolution.zoneFieldTargets
      : undefined
  const unavailableMessage =
    zoneFieldResolution?.status === 'unavailable'
      ? getUnavailableMessage(zoneFieldResolution)
      : undefined

  const handleCancel = () => {
    setVersionReason('')
    onClose()
  }
  const handleCopyThreadsChange = (event: CheckboxChangeEvent) => {
    setCopyThreads(event.target.checked)
  }
  const handleVersionJustificationChange = (
    event: ChangeEvent<HTMLTextAreaElement>
  ) => {
    setVersionReason(event.target.value)
  }

  const handleConfirm = useCallback(async () => {
    if (!sourceTicket || zoneFieldTargets === undefined) {
      return
    }

    setIsCreating(true)
    try {
      const newTicketId = await createTicketVersion({
        sourceTicket,
        zoneFieldTargets,
        versionJustification: versionJustification.trim() || undefined,
      })

      if (copyThreads) {
        try {
          await duplicateActiveThreads(newTicketId, sourceTicket)
        } catch (error) {
          console.warn('Failed to duplicate threads to new version', error)
        }
      }

      messageApi.success(
        `Created version ${sourceTicket.version + 1} based on version ${sourceTicket.version}`
      )
      await onSuccess(newTicketId, sourceTicket)
      setVersionReason('')
      setCopyThreads(false)
      onClose()
    } catch (error) {
      console.error('Failed to create ticket version', error)
      messageApi.error('Failed to create a new ticket version')
    } finally {
      setIsCreating(false)
    }
  }, [
    copyThreads,
    createTicketVersion,
    duplicateActiveThreads,
    messageApi,
    onClose,
    onSuccess,
    sourceTicket,
    zoneFieldTargets,
    versionJustification,
  ])

  return (
    <>
      {contextHolder}
      <Modal
        title='Create New Ticket Version'
        open={isOpen}
        onOk={handleConfirm}
        onCancel={handleCancel}
        okText='Create Version'
        cancelText='Cancel'
        okButtonProps={{ disabled: zoneFieldTargets === undefined }}
        confirmLoading={isCreating}>
        <ContentStack>
          <Alert
            type='info'
            showIcon
            title='The current version will remain unchanged and read-only.'
          />
          {isOpen && sourceTicket && unavailableMessage && (
            <Alert type='warning' showIcon title={unavailableMessage} />
          )}
          <Text type='secondary'>
            The new version is pre-filled with all data from the current
            version. You can review and edit any fields before completing it.
          </Text>
          <Checkbox checked={copyThreads} onChange={handleCopyThreadsChange}>
            Copy comment threads to new version
          </Checkbox>
          <div>
            <Text strong>Reason for new version (optional)</Text>
            <VersionJustificationTextArea
              value={versionJustification}
              onChange={handleVersionJustificationChange}
              placeholder='e.g. Updated coverage assumptions, revised zone boundaries...'
              rows={3}
              maxLength={500}
              showCount
            />
          </div>
        </ContentStack>
      </Modal>
    </>
  )
}

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 8px;
`

const VersionJustificationTextArea = styled(TextArea)`
  margin-top: 0.25rem;
`
